import { useMemo } from "react";

import {
  useNetworkMetadata,
  useNewGivingUI,
  usePermissions,
} from "Flocknote/apiHooks";
import { useIsSuperDuper } from "Flocknote/stateHooks";

import { useFundList } from "Giving/API/queryHooks";
import { useOngoingCampaigns } from "NavBar/API/queryHooks";
import NavLink from "NavBar/components/NavLink";
import NavLinksWrapper from "NavBar/components/NavLinksWrapper";
import NavSection from "NavBar/components/NavSection";
import NavSectionDivider from "NavBar/components/NavSectionDivider";
import useIsSelected from "NavBar/components/useIsSelected";
import {
  navSectionKeys,
  useGivingBatchesRef,
  useGivingCampaignsRef,
  useGivingDonorsRef,
  useGivingFundsRef,
  useGivingRef,
  useGivingSummaryRef,
} from "NavBar/navBarStore";
import {
  useCanSeeGiving,
  useIsGivingBatchesLinkFlashing,
  useIsGivingCampaignsLinkFlashing,
  useIsGivingDonorsLinkFlashing,
  useIsGivingFundsLinkFlashing,
  useIsGivingSquiggleFlashing,
  useIsGivingSummaryLinkFlashing,
} from "NavBar/stateHooks";

import { navColors } from "UI/colors";

import Funds from "./Funds";
import GivingHeaderBadge from "./GivingHeaderBadge";
import LegacyGiving from "./LegacyGiving";
import ListTitle from "./ListTitle";
import OngoingCampaigns from "./OngoingCampaigns";
import SettingsContent from "./SettingsContent";

const Giving = () => {
  const { hideLegacyGivingUI, canViewLegacyGivingUI } = useNewGivingUI();

  const [{ hasFundIt, hasFNC }] = useNetworkMetadata();
  const hasPaymentsOnly = !hasFNC && hasFundIt;

  const [{ canViewAllGivingData, canSeeCampaignsNav, canAddGifts }] =
    usePermissions();
  const settingsSelected = useIsSelected("/giving/settings");
  const isSuperDuper = useIsSuperDuper();
  const givingRef = useGivingRef();
  const givingSummaryRef = useGivingSummaryRef();
  const givingFundsRef = useGivingFundsRef();
  const givingCampaignsRef = useGivingCampaignsRef();
  const givingBatchesRef = useGivingBatchesRef();
  const givingDonorsRef = useGivingDonorsRef();
  const isGivingSummaryFlashing = useIsGivingSummaryLinkFlashing();
  const isGivingFundsFlashing = useIsGivingFundsLinkFlashing();
  const isGivingCampaignsFlashing = useIsGivingCampaignsLinkFlashing();
  const isGivingBatchesFlashing = useIsGivingBatchesLinkFlashing();
  const isGivingDonorsFlashing = useIsGivingDonorsLinkFlashing();
  const isGivingSquiggleFlashing = useIsGivingSquiggleFlashing();
  const canSeeGiving = useCanSeeGiving();
  const { showOngoingCampaigns } = useOngoingCampaigns();
  // Include archived funds — this only gates a nav divider on whether the
  // network has any funds at all, and an archived fund still counts.
  const funds = useFundList({ includeArchived: true });

  const hasFunds = canSeeGiving && hasFundIt;
  const hasAnyFunds = funds.length > 0;

  const firstVisibleLink = useMemo(() => {
    if (canViewAllGivingData) return "/giving";
    if (canSeeCampaignsNav) return "/giving/campaigns";
    return null;
  }, [canViewAllGivingData, canSeeCampaignsNav]);

  const canSeeSettings =
    isSuperDuper ||
    (canViewLegacyGivingUI &&
      (canViewAllGivingData || canAddGifts || canSeeCampaignsNav));

  const canOnlyAddGifts =
    canAddGifts && !canViewAllGivingData && !canSeeCampaignsNav;

  if (!hideLegacyGivingUI)
    return <LegacyGiving canSeeSettings={canSeeSettings} />;

  return (
    <NavSection
      bottomPadding={hasFunds ? 0 : 4}
      label="Giving"
      data-cy="NavBar_GivingMenu"
      icon="money-bill-wave far"
      color={navColors.giving}
      navSectionKey={navSectionKeys.giving}
      SettingsContent={canSeeSettings ? SettingsContent : undefined}
      settingsSelected={settingsSelected}
      headerTextRef={givingRef}
      isSquiggleFlashing={isGivingSquiggleFlashing}
      defaultRoute={firstVisibleLink}
      HeaderSuffixComponent={
        canViewLegacyGivingUI ? GivingHeaderBadge : undefined
      }
    >
      <NavLinksWrapper minWidth={80}>
        {canViewAllGivingData && (
          <>
            <NavLink
              navSectionKey={navSectionKeys.giving}
              to="giving"
              data-cy="NavBar_GivingSummary"
              icon="chart-pie fas"
              ref={givingSummaryRef}
              isFlashing={isGivingSummaryFlashing}
            >
              Summary
            </NavLink>
            <NavLink
              ref={givingFundsRef}
              navSectionKey={navSectionKeys.giving}
              to="giving/funds"
              data-cy="NavBar_GivingFunds"
              icon="piggy-bank fas"
              isFlashing={isGivingFundsFlashing}
            >
              Funds
            </NavLink>
          </>
        )}
        {canSeeCampaignsNav && (
          <NavLink
            ref={givingCampaignsRef}
            navSectionKey={navSectionKeys.giving}
            to="giving/campaigns"
            data-cy="NavBar_Campaigns"
            icon="bullhorn fas"
            isFlashing={isGivingCampaignsFlashing}
          >
            Campaigns
          </NavLink>
        )}
        {canOnlyAddGifts && (
          <NavLink
            ref={givingBatchesRef}
            navSectionKey={navSectionKeys.giving}
            to="giving/batches/new-batch"
            data-cy="NavBar_GivingBatches"
            icon="layer-group fas"
            isFlashing={isGivingBatchesFlashing}
          >
            Batches
          </NavLink>
        )}
        {canViewAllGivingData && (
          <>
            <NavLink
              ref={givingBatchesRef}
              navSectionKey={navSectionKeys.giving}
              to="giving/batches"
              data-cy="NavBar_GivingBatches"
              icon="layer-group fas"
              isFlashing={isGivingBatchesFlashing}
            >
              {hasPaymentsOnly ? "Deposits" : "Batches"}
            </NavLink>
            <NavLink
              navSectionKey={navSectionKeys.giving}
              to="giving/transactions"
              data-cy="NavBar_GivingTransactions"
              icon="exchange-alt fas"
            >
              Transactions
            </NavLink>
            <NavLink
              ref={givingDonorsRef}
              navSectionKey={navSectionKeys.giving}
              to="giving/donors"
              data-cy="NavBar_GivingDonors"
              icon="users fas"
              isFlashing={isGivingDonorsFlashing}
            >
              Donors
            </NavLink>
          </>
        )}
      </NavLinksWrapper>
      <OngoingCampaigns />
      {hasFunds && (
        <>
          {hasAnyFunds && <NavSectionDivider />}
          {hasAnyFunds && showOngoingCampaigns && (
            <ListTitle title="Funds" icon="piggy-bank fas" />
          )}
          <Funds />
        </>
      )}
    </NavSection>
  );
};

export default Giving;
